import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { invitationKey, joinInvitation } from '../lib/invitationJoin';

// Repeated mounts share an unsettled request, including Back/Forward and StrictMode.
// A settled retry always reads membership again before any insert.
const inFlight = new Map();
function subscribeToJoin(scope, userId, joinKey, listener, isCurrent) {
  let operation = inFlight.get(scope);
  if (!operation) {
    operation = {listeners:new Map(), state:{phase:'joining', competition:null}};
    inFlight.set(scope, operation);
    const publish = state => {
      operation.state = state;
      for (const [notify, current] of operation.listeners) if (current()) notify(state);
    };
    operation.promise = Promise.resolve().then(() => joinInvitation({
      client:supabase, userId, joinKey,
      isCurrent:() => [...operation.listeners.values()].some(current => current()),
      onCompetition:competition => publish({phase:'joining', competition}),
    })).then(result => {
      if (result) publish(result);
      else if ([...operation.listeners.values()].some(current => current())) {
        publish({phase:'error', retryable:true, competition:operation.state.competition, message:'The invitation check was interrupted. Try again to check your entry.'});
      }
    }).finally(() => {if (inFlight.get(scope) === operation) inFlight.delete(scope);});
  }
  operation.listeners.set(listener, isCurrent);
  listener(operation.state);
  return () => operation.listeners.delete(listener);
}

export default function useInvitationJoin(userId) {
  const {search} = useLocation();
  const [,setSearchParams] = useSearchParams();
  const [pendingIntent,setPendingIntent] = useState(() => invitationKey(search, localStorage.getItem('pendingJoin')));
  const joinKey = invitationKey(search, pendingIntent);
  const scope = userId && joinKey !== null ? `${userId}:${joinKey}` : null;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [attempt,setAttempt] = useState(0);
  const [state,setState] = useState(null);

  useEffect(() => {
    if (!scope) {setState(null); return;}
    setPendingIntent(joinKey);
    // New explicit links replace stale local intent. A blank link cannot revive it.
    if (joinKey) localStorage.setItem('pendingJoin', joinKey);
    else localStorage.removeItem('pendingJoin');
    let mounted = true;
    const isCurrent = () => mounted && currentScope.current === scope;
    const unsubscribe = subscribeToJoin(scope, userId, joinKey, result => {
      if (!isCurrent()) return;
      setState({...result, scope});
      if (result.phase === 'confirmed') {
        if (localStorage.getItem('pendingJoin') === joinKey) localStorage.removeItem('pendingJoin');
        window.dispatchEvent(new Event('focus'));
      }
    }, isCurrent);
    return () => {mounted = false; unsubscribe();};
  }, [scope, attempt]);

  const dismiss = () => {
    if (localStorage.getItem('pendingJoin') === joinKey) localStorage.removeItem('pendingJoin');
    setSearchParams(params => {params.delete('join'); return params;}, {replace:true});
    setPendingIntent(null);
    setState(null);
  };
  return {state:state?.scope === scope ? state : scope ? {phase:'joining'} : null,
    retry:() => {if(state?.phase === 'error')setAttempt(value => value+1);}, dismiss};
}
